import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Camera, User as UserIcon, Mail, Phone, Briefcase, Save, Lock } from "lucide-react";
import api from "../../api";
import { setUser } from "../../store/slices/authSlice";
import UserAvatar from "../common/UserAvatar";

/**
 * MyProfile is where the logged-in agent / admin / PA edits their own
 * profile. The profile picture set here is used wherever the user's name
 * is displayed across the CRM (sidebar, agent cards, property details).
 */
const MyProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    name: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    title: "",
  });
  const [picFile, setPicFile] = useState(null);
  const [picPreview, setPicPreview] = useState("");
  const [picRemoved, setPicRemoved] = useState(false);
  const [picError, setPicError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const res = await api.get("role-management/profile/");
        if (cancelled) return;
        const data = res.data || {};
        setForm({
          name: data.name || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone_number: data.phone_number || "",
          title: data.title || "",
        });
        setPicPreview(data.profile_pic_url || "");
      } catch (err) {
        console.error("Failed to load profile", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  // Free up object URLs when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (picPreview && picPreview.startsWith("blob:")) {
        URL.revokeObjectURL(picPreview);
      }
    };
  }, [picPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePicChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPicError("Please choose an image file (JPG, PNG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPicError("Image must be smaller than 5 MB");
      return;
    }
    setPicError("");
    setPicFile(file);
    setPicPreview(URL.createObjectURL(file));
    setPicRemoved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null) fd.append(key, value);
      });
      if (picFile) {
        fd.append("profile_pic", picFile);
      } else if (picRemoved) {
        fd.append("clear_profile_pic", "true");
      }

      const res = await api.patch("role-management/profile/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updated = res.data || {};

      // Update redux + localStorage so sidebar avatar refreshes immediately.
      const mergedUser = {
        ...(user || {}),
        ...updated,
        // The login response stores the picture under `profile_pic_url`; mirror
        // that here so all components keep working consistently.
        profile_pic_url: updated.profile_pic_url || "",
      };
      dispatch(setUser(mergedUser));
      setPicFile(null);
      setPicRemoved(false);
      setPicPreview(updated.profile_pic_url || "");
      toast.success("Profile updated successfully");
    } catch (err) {
      const message = err?.response?.data?.detail || "Failed to update profile";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-100 rounded-2xl" />
          <div className="h-64 bg-gray-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">My Profile</h2>
        <p className="text-gray-600 text-sm sm:text-base mt-1">
          Update your photo and personal details. Your profile picture appears wherever your name is shown.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Picture */}
        <section className="bg-white rounded-2xl shadow border border-gray-100 p-6">
          <div className="flex items-center mb-5">
            <Camera className="w-5 h-5 text-[#55C5D0] mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Profile Picture</h3>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <UserAvatar
              src={picPreview}
              name={form.name || `${form.first_name} ${form.last_name}`.trim() || user?.name}
              size={112}
              bgColor="#575955"
              className="border-4 border-white shadow-md"
            />
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePicChange}
                className="hidden"
              />
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be] transition-colors"
                >
                  {picPreview ? "Change Photo" : "Upload Photo"}
                </button>
                {picPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setPicFile(null);
                      setPicPreview("");
                      setPicRemoved(true);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">JPG or PNG, up to 5 MB. Square images look best.</p>
              {picError && <p className="text-xs text-red-500 mt-1">{picError}</p>}
            </div>
          </div>
        </section>

        {/* Personal Details */}
        <section className="bg-white rounded-2xl shadow border border-gray-100 p-6">
          <div className="flex items-center mb-5">
            <UserIcon className="w-5 h-5 text-[#55C5D0] mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Personal Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Briefcase className="w-4 h-4 text-gray-400" /> Title
              </label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. Senior Sales Agent"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Mail className="w-4 h-4 text-gray-400" /> Email
              </label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Phone className="w-4 h-4 text-gray-400" /> Phone Number
              </label>
              <input
                type="tel"
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                placeholder="04xx xxx xxx"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
              />
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#55C5D0] text-white rounded-xl text-sm font-semibold hover:bg-[#3bb3be] transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>

      {/* Two-factor authentication now lives on Settings > Security. This is a
          signpost for anyone who still looks for it here. */}
      <section className="bg-white rounded-2xl shadow border border-gray-100 p-6">
        <div className="flex items-center mb-2">
          <Lock className="w-5 h-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">Two-Factor Authentication</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Two-factor authentication has moved to Settings, under the Security tab.
        </p>
        <button
          type="button"
          onClick={() => navigate("/settings?tab=security")}
          className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be]"
        >
          Go to Security settings
        </button>
      </section>
    </div>
  );
};

export default MyProfile;
